import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import ManagerSidebar from './components/ManagerSidebar';
import ManagerBottomNav from './components/ManagerBottomNav';
import PushPrompt from '../../components/PushPrompt';
import ManagerMobileHeader from './components/ManagerMobileHeader';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { calculateManagerBonus } from '../../utils/bonusCalculator';
import LangModal from '../../components/LangModal';
import ActivenessDistributionCard from '../../components/ActivenessDistributionCard';

const SERVER_COLORS = {
  MENA: '#f97316',
  FR: '#3b82f6',
  IT: '#22c55e',
  DE: '#a855f7',
};

const LEVEL_CONFIG = {
  Junior: { color: '#6b7280', bg: 'rgba(107,114,128,0.15)' },
  Rising: { color: '#3B82F6', bg: 'rgba(59,130,246,0.15)' },
  Senior: { color: '#FF6B00', bg: 'rgba(255,107,0,0.15)' },
  Elite: { color: '#FFD700', bg: 'rgba(255,215,0,0.15)' },
};

const fmtM = (n) => {
  if (n == null) return '0';
  if (n >= 1_000_000) return (n / 1_000_000)?.toFixed(2) + 'M';
  if (n >= 1_000) return (n / 1_000)?.toFixed(1) + 'K';
  return String(Math.round(n));
};

const fmtUSD = (n) => {
  if (!n) return '$0';
  return '$' + Math.round(n)?.toLocaleString('fr-FR');
};

const ServerBadge = ({ server }) => (
  <span
    className="text-xs font-bold px-2 py-0.5 rounded-md"
    style={{ backgroundColor: `${SERVER_COLORS?.[server] || '#888'}22`, color: SERVER_COLORS?.[server] || '#888', fontFamily: 'Sora, sans-serif' }}
  >
    {server}
  </span>
);

const TierBadge = ({ tier }) => (
  <span
    className="text-xs font-bold px-1.5 py-0.5 rounded"
    style={{ backgroundColor: 'rgba(229,32,42,0.12)', color: '#E5202A', fontFamily: 'Sora, sans-serif' }}
  >
    T{tier}
  </span>
);

const SkeletonCard = () => (
  <div className="rounded-xl p-5 border animate-pulse" style={{ backgroundColor: '#111111', borderColor: '#1f1f1f' }}>
    <div className="h-4 bg-white/10 rounded w-1/3 mb-3" />
    <div className="h-8 bg-white/10 rounded w-2/3 mb-2" />
    <div className="h-3 bg-white/10 rounded w-full mb-1" />
    <div className="h-2 bg-white/10 rounded w-full" />
  </div>
);

const ManagerDashboard = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [loading, setLoading] = useState(true);

  const [user, setUser] = useState(null);
  const [managerProfile, setManagerProfile] = useState(null);
  const [managerCreators, setManagerCreators] = useState([]);
  const [allDailyData, setAllDailyData] = useState([]);
  const [incrementalConfig, setIncrementalConfig] = useState([]);
  const [activenessConfig, setActivenessConfig] = useState([]);
  const [incrementalRate, setIncrementalRate] = useState(50);
  const [rankupConditions, setRankupConditions] = useState({});

  // Dynamic dates
  const now = new Date();
  const year = now?.getFullYear();
  const month = now?.getMonth();
  const monthStart = `${year}-${String(month + 1)?.padStart(2, '0')}-01`;
  const lastDay = new Date(year, month + 1, 0)?.getDate();
  const monthEnd = `${year}-${String(month + 1)?.padStart(2, '0')}-${String(lastDay)?.padStart(2, '0')}`;
  const currentPeriod = `${year}-${String(month + 1)?.padStart(2, '0')}`;
  const localeMap = { fr: 'fr-FR', en: 'en-US', it: 'it-IT', de: 'de-DE' };
  const monthName = now?.toLocaleString(localeMap[i18n.language] || 'en-US', { month: 'long', year: 'numeric' });
  const monthNameCap = monthName?.charAt(0)?.toUpperCase() + monthName?.slice(1);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        // Step 1: get auth user
        const { data: { user: authUser } } = await supabase?.auth?.getUser();
        if (!authUser) { navigate('/login-authentication'); return; }
        setUser(authUser);

        // Step 2: get manager profile
        const { data: profile } = await supabase?.from('users')?.select('*')?.eq('id', authUser?.id)?.single();
        setManagerProfile(profile);

        // Step 3: get manager's creators (for contract/level info)
        const { data: creators } = await supabase?.from('creators')?.select('tiktok_id, username, server, group_name, level, contract_end, tier_last_month, last_live_date, agency_status')?.eq('manager_id', authUser?.id)?.neq('agency_status', 'left_agency');
        setManagerCreators(creators || []);

        // Step 4: fetch creator_current_month by manager_id
        const { data: currentData } = await supabase?.from('creator_current_month')?.select('*')?.eq('manager_id', authUser?.id)?.eq('period', currentPeriod);

        setAllDailyData(currentData || []);

        // Step 5: Fetch incremental_config
        const { data: configData } = await supabase?.from('incremental_config')?.select('server, completion_tiers, target_100pct')?.is('period', null)?.or(`period.eq.${currentPeriod}`);
        setIncrementalConfig(configData || []);

        // Step 6: Fetch activeness_config + rankup_config conditions
        const { data: actvConfig } = await supabase?.from('activeness_config')?.select('*');
        setActivenessConfig(actvConfig || []);

        const { data: rcData } = await supabase?.from('rankup_config')?.select('server, tier, min_diamonds, min_live_days, min_live_hours');
        const rcMap = {};
        rcData?.forEach(rc => { rcMap[`${rc.server}-${rc.tier}`] = rc; });
        setRankupConditions(rcMap);

        // Step 7: Fetch incremental_rate from manager_rates for current period
        const { data: rateData } = await supabase?.from('manager_rates')?.select('incremental_rate')?.eq('manager_id', authUser?.id)?.eq('period', currentPeriod)?.single();
        setIncrementalRate(rateData?.incremental_rate || 50);

      } catch (e) {
        console.error('Manager dashboard load error:', e);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  // Build creatorMap from creator_current_month data
  const creatorMap = useMemo(() => {
    const creatorsLookup = {};
    managerCreators?.forEach(c => { creatorsLookup[c?.tiktok_id] = c; });
    const map = {};
    allDailyData?.forEach(row => {
      map[row.tiktok_id] = {
        tiktok_id: row?.tiktok_id,
        username: row?.username,
        server: row?.server,
        group_name: row?.group_name,
        level: creatorsLookup[row?.tiktok_id]?.level || 'Junior',
        total_diamonds: row?.diamonds || 0,
        live_days: row?.live_days || 0,
        tier_m1: row?.tier_m1,
        tier_current: row?.tier_current,
        bonus_rankup: row?.bonus_rankup || 0,
        activeness_level: row?.activeness_level || 0,
        bonus_activeness: row?.bonus_activeness || 0,
        rankup_eligible: row?.rankup_eligible,
        live_duration_minutes: row?.live_duration_minutes || 0,
        is_active: row?.is_active,
        is_new_recruit: row?.is_new_recruit,
        cashback_amount: row?.cashback_amount || 0,
        projected_diamonds: row?.projected_diamonds || 0,
        gap_to_objective: row?.gap_to_objective,
        objective_diamonds: row?.objective_diamonds,
        evolution_pct: row?.evolution_pct,
        bonus: 0,
        charge: 0,
        incremental_bonus: 0,
      };
    });
    // Include creators not yet in current month data
    managerCreators?.forEach(c => {
      if (!map?.[c?.tiktok_id]) {
        map[c.tiktok_id] = { ...c, total_diamonds: 0, live_days: 0, tier_current: 1, activeness_level: 0, bonus_rankup: 0, bonus_activeness: 0, bonus: 0, charge: 0, incremental_bonus: 0, is_active: false };
      }
    });
    return map;
  }, [allDailyData, managerCreators]);

  const creatorList = useMemo(() => Object.values(creatorMap), [creatorMap]);

  const joursInjectes = useMemo(() => {
    return allDailyData?.length > 0 ? (allDailyData?.[0]?.days_elapsed || 1) : 1;
  }, [allDailyData]);

  // Servers manager has creators on
  const managerServers = useMemo(() => {
    const servers = [...new Set(managerCreators.map(c => c.server).filter(Boolean))];
    return servers;
  }, [managerCreators]);

  // Per-server performance data
  const serverPerf = useMemo(() => {
    return managerServers?.map(server => {
      const serverDiamonds = allDailyData?.filter(r => r?.server === server)?.reduce((sum, r) => sum + (r?.diamonds || 0), 0);

      const objKey = `objective_${server?.toLowerCase()}`;
      const objective = managerProfile?.[objKey] || 0;
      const pct = objective > 0 ? (serverDiamonds / objective) * 100 : null;

      // Find palier from incremental_config
      const config = incrementalConfig?.find(c => c?.server === server);
      let palier = null;
      if (config?.completion_tiers && pct != null) {
        const tiers = Array.isArray(config?.completion_tiers) ? config?.completion_tiers : [];
        for (const t of [...tiers]?.sort((a, b) => b?.pct - a?.pct)) {
          if (pct >= t?.pct) { palier = t; break; }
        }
      }

      const bonusEstime = palier ? (serverDiamonds / 100) * (palier?.bonus_pct || palier?.bonus || 0) / 100 : 0;

const projection = allDailyData
  ?.filter(r => r?.server === server)
  ?.reduce((sum, r) => sum + (r?.projected_diamonds || 0), 0) || 0;

      return { server, serverDiamonds, objective, pct, palier, bonusEstime, projection };
    });
  }, [managerServers, allDailyData, managerProfile, incrementalConfig]);

  // Creator stats
  const totalCreateurs = creatorList?.length;

  // Build activeThisMonth Set from creator_current_month is_active flag
  const activeThisMonth = useMemo(() => {
    return new Set(
      allDailyData
        .filter(r => r?.is_active === true)
        .map(r => r?.tiktok_id)
    );
  }, [allDailyData]);

  const actifsCount = activeThisMonth?.size || 0;
  const inactifsCount = totalCreateurs - actifsCount;
  const goLiveRate = totalCreateurs > 0 ? Math.round((actifsCount / totalCreateurs) * 100) : 0;
  const avgDiamonds = actifsCount > 0
    ? creatorList?.filter(c => activeThisMonth?.has(c?.tiktok_id))?.reduce((s, c) => s + c?.total_diamonds, 0) / actifsCount
    : 0;

  // Tier distribution — M and M-1 combined
  const tierDist = useMemo(() => {
    const distM = {};
    const distM1 = {};
    for (let i = 1; i <= 10; i++) { distM[i] = 0; distM1[i] = 0; }
    creatorList?.forEach(c => {
      const t = Math.min(Math.max(c?.tier_current || 1, 1), 10);
      const t1 = Math.min(Math.max(c?.tier_m1 || 1, 1), 10);
      distM[t]++;
      distM1[t1]++;
    });
    return Object.keys(distM)?.map(tier => ({
      tier: `T${tier}`,
      current: distM[tier],
      previous: distM1[tier],
    }));
  }, [creatorList]);

  // Level distribution
  const levelDist = useMemo(() => {
    const dist = { Junior: 0, Rising: 0, Senior: 0, Elite: 0 };
    creatorList?.forEach(c => {
      const lvl = c?.level || 'Junior';
      if (dist?.[lvl] !== undefined) dist[lvl]++;
    });
    return dist;
  }, [creatorList]);

  // Activeness distribution
  const activenessDistribution = useMemo(() => {
    const dist = { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    creatorList?.forEach(c => { dist[c.activeness_level || 0]++; });
    return dist;
  }, [creatorList]);

  const totalBonusActiveness = useMemo(() =>
    creatorList?.reduce((sum, c) => sum + (c?.bonus_activeness || 0), 0), [creatorList]);

  // Rank-up stats
  const rankingUp = useMemo(() => creatorList?.filter(c => (c?.tier_current || 1) > (c?.tier_m1 || 1))?.length, [creatorList]);
  const maintaining = useMemo(() => creatorList?.filter(c => (c?.tier_current || 1) === (c?.tier_m1 || 1) && (c?.tier_m1 || 1) >= 2)?.length, [creatorList]);
  const tierLost = useMemo(() => creatorList?.filter(c => (c?.tier_current || 1) < (c?.tier_m1 || 1))?.length, [creatorList]);
  const totalBonusRankup = useMemo(() => creatorList?.reduce((sum, c) => sum + (c?.bonus_rankup || 0), 0), [creatorList]);

  // Bonus Net Estimé using manager formula from creator_monthly
  const [monthlyBonusData, setMonthlyBonusData] = useState([]);
  // Violations state
  const [violationStats, setViolationStats] = useState({ thisMonth: 0, activeBans: 0, ticketsOpened: 0, withdrawn: 0, notAppealed: 0 });
  const [activeBansList, setActiveBansList] = useState([]);
  const [violationsLoading, setViolationsLoading] = useState(true);

  // WhatsApp alerts state
  const [whatsappAlerts, setWhatsappAlerts] = useState([]);
  const [whatsappAlertsLoading, setWhatsappAlertsLoading] = useState(false);

  const [whatsappEditId, setWhatsappEditId] = useState(null);
  const [whatsappEditValue, setWhatsappEditValue] = useState('');
  const [whatsappEditSaving, setWhatsappEditSaving] = useState(false);
  const [whatsappEditError, setWhatsappEditError] = useState('');

  useEffect(() => {
    if (!user || !currentPeriod) return;
    const fetchMonthlyBonus = async () => {
      const { data } = await supabase?.from('creator_monthly_history')?.select('tiktok_id, total_bonus, charge, incremental_bonus')?.eq('manager_id', user?.id)?.eq('period', currentPeriod);
      setMonthlyBonusData(data || []);
    };
    fetchMonthlyBonus();
  }, [user, currentPeriod]);

  // Fetch WhatsApp alerts: creators managed by this manager with no WhatsApp
  useEffect(() => {
    if (!user || !managerProfile) return;
    const fetchWhatsappAlerts = async () => {
      setWhatsappAlertsLoading(true);
      try {
        // Get distinct tiktok_ids from creator_current_month for this manager
        const { data: currentMonthRows } = await supabase?.from('creator_current_month')?.select('tiktok_id, username, server')?.eq('manager_id', user?.id);

        if (!currentMonthRows || currentMonthRows?.length === 0) {
          setWhatsappAlerts([]);
          setWhatsappAlertsLoading(false);
          return;
        }

        const tiktokIds = currentMonthRows?.map(r => r?.tiktok_id);

        // Cross-reference with creators table for whatsapp IS NULL or empty
        const { data: creatorsData } = await supabase?.from('creators')?.select('tiktok_id, username, whatsapp')?.in('tiktok_id', tiktokIds);

        const noWaCreators = (creatorsData || [])?.filter(c => !c?.whatsapp || c?.whatsapp?.trim() === '');

        const alerts = noWaCreators?.map(c => ({
          ...c,
          username: c?.username || currentMonthRows?.find(u => u?.tiktok_id === c?.tiktok_id)?.username || c?.tiktok_id,
          server: currentMonthRows?.find(u => u?.tiktok_id === c?.tiktok_id)?.server || null,
        }));

        setWhatsappAlerts(alerts);
      } catch (err) {
        console.error('WhatsApp alerts fetch error:', err);
      } finally {
        setWhatsappAlertsLoading(false);
      }
    };
    fetchWhatsappAlerts();
  }, [user, managerProfile, currentPeriod]);

  // Fetch violations for manager's creators
  useEffect(() => {
    if (!user || creatorList?.length === 0) return;
    const fetchViolations = async () => {
      setViolationsLoading(true);
      try {
        const tiktokIds = creatorList.map(c => c.tiktok_id).filter(Boolean);
        if (tiktokIds.length === 0) { setViolationsLoading(false); return; }

        const currentMonth = new Date().toISOString().substring(0, 7);
        const monthStart = `${currentMonth}-01T00:00:00`;
        const nowISO = new Date().toISOString();

        const [thisMonthRes, activeBansRes, ticketsRes, banListRes, withdrawnRes, notAppealedRes] = await Promise.all([
          supabase.from('violations').select('id', { count: 'exact', head: true }).in('tiktok_id', tiktokIds).gte('punishment_time', monthStart),
          supabase.from('violations').select('id', { count: 'exact', head: true }).in('tiktok_id', tiktokIds).eq('penalty_status', 'In effect'),
          supabase.from('violations').select('id', { count: 'exact', head: true }).in('tiktok_id', tiktokIds).eq('appeal_status', 'Ticket opened'),
          supabase.from('violations').select('id, creator_username, creator_nickname, server, violation_type, tiktok_id').in('tiktok_id', tiktokIds).eq('penalty_status', 'In effect').gt('recovery_time', nowISO).limit(5),
          supabase.from('violations').select('id', { count: 'exact', head: true }).in('tiktok_id', tiktokIds).eq('penalty_status', 'Withdrawn').gte('punishment_time', monthStart),
          supabase.from('violations').select('id', { count: 'exact', head: true }).in('tiktok_id', tiktokIds).eq('appeal_status', 'Not appealed').eq('penalty_status', 'In effect').gte('punishment_time', monthStart),
        ]);

        setViolationStats({
          thisMonth: thisMonthRes.count || 0,
          activeBans: activeBansRes.count || 0,
          ticketsOpened: ticketsRes.count || 0,
          withdrawn: withdrawnRes.count || 0,
          notAppealed: notAppealedRes.count || 0,
        });
        setActiveBansList(banListRes.data || []);
      } catch (err) {
        console.error('Violations fetch error:', err);
      } finally {
        setViolationsLoading(false);
      }
    };
    fetchViolations();
  }, [user, creatorList]);

  const handleWhatsappAlertSave = async (tiktokId) => {
    const val = whatsappEditValue?.trim();
    if (val && !/^\+[0-9]{8,15}$/?.test(val)) {
      setWhatsappEditError('Format invalide. Doit commencer par + et contenir 8-15 chiffres.');
      return;
    }
    setWhatsappEditSaving(true);
    setWhatsappEditError('');
    const { error } = await supabase?.from('creators')?.update({ whatsapp: val || null })?.eq('tiktok_id', tiktokId);
    if (!error) {
      setWhatsappAlerts(prev => prev?.filter(c => c?.tiktok_id !== tiktokId));
      setWhatsappEditId(null);
      setWhatsappEditValue('');
    } else {
      setWhatsappEditError('Erreur lors de la sauvegarde.');
    }
    setWhatsappEditSaving(false);
  };

  const totalBonusNet = useMemo(() => {
    if (monthlyBonusData?.length > 0) {
      return monthlyBonusData?.reduce((sum, r) => {
        return sum + calculateManagerBonus(r?.total_bonus || 0, r?.charge || 0, r?.incremental_bonus || 0, incrementalRate);
      }, 0);
    }
    // Fallback: use creatorMap fields if monthly not yet available
    return Object.values(creatorMap)?.reduce((sum, c) => {
      return sum + calculateManagerBonus(c?.bonus || 0, c?.charge || 0, c?.incremental_bonus || 0, incrementalRate);
    }, 0);
  }, [monthlyBonusData, creatorMap, incrementalRate]);

  const totalBonus = totalBonusActiveness + totalBonusRankup;
  const projectionBonus = joursInjectes > 0 ? (totalBonus * 31) / joursInjectes : 0;

  // Top 5 performers
  const top5 = useMemo(() =>
    [...creatorList]?.sort((a, b) => b?.total_diamonds - a?.total_diamonds)?.slice(0, 5),
    [creatorList]);

  // Alerts
  const inactifs = creatorList?.filter(c => {
    const isActive = activeThisMonth?.has(c?.tiktok_id);
    return !isActive;
  });

  // ── ALERTS: 3 bonus-related + inactifs + régression + contrats ──

  // 1. Activeness: proches du palier suivant (≤3j ou ≤10h)
  const activenessClose = useMemo(() => {
    const thresholds = [
      { level: 1, days: 8, hours: 20 }, { level: 2, days: 11, hours: 30 },
      { level: 3, days: 15, hours: 40 }, { level: 4, days: 18, hours: 60 },
      { level: 5, days: 22, hours: 80 },
    ];
    return creatorList
      ?.filter(c => c?.activeness_level != null && c?.activeness_level < 5 && c?.is_active)
      ?.map(c => {
        const nextLevel = thresholds?.find(t => t.level === (c.activeness_level || 0) + 1);
        if (!nextLevel) return null;
        const liveDays = c.live_days || 0;
        const ccmRow = allDailyData?.find(d => d.tiktok_id === c.tiktok_id);
        const liveHours = Math.round(((ccmRow?.live_duration_minutes || 0) / 60) * 10) / 10;
        const daysGap = nextLevel.days - liveDays;
        const hoursGap = Math.round((nextLevel.hours - liveHours) * 10) / 10;
        if (daysGap <= 0 && hoursGap <= 0) return null;
        if (daysGap > 3 || hoursGap > 10) return null;
        return { ...c, next_level: nextLevel.level, days_gap: Math.max(0, daysGap), hours_gap: Math.max(0, hoursGap), live_days: liveDays, live_hours: liveHours };
      })
      ?.filter(Boolean)
      ?.sort((a, b) => (a.days_gap + a.hours_gap / 10) - (b.days_gap + b.hours_gap / 10))
      ?.slice(0, 15) || [];
  }, [creatorList, allDailyData]);

  // 2. Maintien du tier: créateurs tier_m1 >= 3 qui risquent de perdre leur tier
  const nearMaintain = useMemo(() => {
    return creatorList
      ?.filter(c => (c?.tier_m1 || 0) >= 3 && c?.is_active)
      ?.map(c => {
        // Seuil de maintien = min_diamonds du tier actuel (tier_m1) depuis rankup_config
        const tierM1 = c.tier_m1 || 0;
        const rc = rankupConditions[`${c.server}-${tierM1}`];
        const maintainThreshold = rc?.min_diamonds || 0;
        if (maintainThreshold <= 0) return null;
        const diamonds = c.total_diamonds || 0;
        const gap = maintainThreshold - diamonds;
        if (gap <= 0) return null; // already maintained
        return { ...c, maintain_threshold: maintainThreshold, maintain_gap: gap };
      })
      ?.filter(Boolean)
      ?.sort((a, b) => a.maintain_gap - b.maintain_gap)
      ?.slice(0, 15) || [];
  }, [creatorList]);

  // 3. Rankup: proches du tier suivant (afficher diamonds manquants) + rank up sans condition
  const nearNextTier = useMemo(() => {
    const nearDiamonds = creatorList
      ?.filter(c => c?.objective_diamonds > 0 && c?.total_diamonds > 0 && c?.is_active)
      ?.map(c => ({ ...c, gap: c.objective_diamonds - c.total_diamonds, alertType: 'diamonds' }))
      ?.filter(c => c.gap > 0 && c.gap < c.objective_diamonds * 0.3)
      ?.sort((a, b) => a.gap - b.gap)
      ?.slice(0, 15) || [];

    const needCondition = creatorList
      ?.filter(c => c?.tier_current > c?.tier_m1 && c?.rankup_eligible === false)
      ?.map(c => {
        const cond = rankupConditions[`${c.server}-${c.tier_m1}`];
        if (!cond) return null;
        const missingDays = Math.max((cond.min_live_days || 0) - (c.live_days || 0), 0);
        const liveHours = (c.live_duration_minutes || 0) / 60;
        const missingHours = Math.max((parseFloat(cond.min_live_hours) || 0) - liveHours, 0);
        if (missingDays <= 0 && missingHours <= 0) return null;
        return { ...c, missingDays, missingHours: Math.round(missingHours * 10) / 10, alertType: 'condition' };
      })
      ?.filter(Boolean)
      ?.sort((a, b) => (a.missingDays + a.missingHours) - (b.missingDays + b.missingHours)) || [];

    return [...needCondition, ...nearDiamonds]?.slice(0, 20);
  }, [creatorList, rankupConditions]);

  // Inactifs
  const inRegression = useMemo(() =>
    creatorList?.filter(c => c?.evolution_pct != null && c?.evolution_pct < -30),
    [creatorList]);

  // Contrats expirants (30 jours)
  const contractExpiring = useMemo(() => {
    const today = new Date();
    const in30 = new Date(today.getTime() + 30 * 86400000);
    return managerCreators
      ?.filter(c => {
        if (!c?.contract_end) return false;
        const d = new Date(c.contract_end);
        return d >= today && d <= in30;
      })
      ?.sort((a, b) => new Date(a.contract_end) - new Date(b.contract_end))
      ?.slice(0, 10) || [];
  }, [managerCreators]);

  const hasAlerts = inactifs?.length > 0 || contractExpiring?.length > 0 || activenessClose?.length > 0 || nearNextTier?.length > 0 || nearMaintain?.length > 0 || inRegression?.length > 0;

  const getProgressColor = (pct) => {
    if (pct == null) return '#6b7280';
    if (pct >= 90) return '#22c55e';
    if (pct >= 70) return '#f97316';
    return '#ef4444';
  };

  if (loading) {
    return (
      <div className="flex h-[100dvh]" style={{ backgroundColor: '#0a0a0a' }}>
        <ManagerSidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} managerName="" />
        <div className="flex-1 p-4 md:p-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
          {[...Array(6)]?.map((_, i) => <SkeletonCard key={i} />)}
        </div>
        <ManagerBottomNav />
      </div>
    );
  }

  return (
    <div className="flex h-[100dvh] overflow-hidden" style={{ backgroundColor: '#0a0a0a' }}>
      {managerProfile && !managerProfile?.lang && <LangModal onClose={() => setManagerProfile(prev => ({ ...prev, lang: 'fr' }))} />}
      <ManagerSidebar
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        managerName={managerProfile?.name || managerProfile?.email || ''}
      />
      <main className="flex-1 overflow-y-auto pb-16 md:pb-0">
        <ManagerMobileHeader title="Dashboard" />
        <div className="p-3 md:p-6 max-w-7xl mx-auto space-y-5 md:space-y-8">

          {/* SECTION 1 — HEADER */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h1 className="font-bold text-white" style={{ fontFamily: 'Sora, sans-serif', fontSize: 'clamp(18px, 4vw, 24px)' }}>
                  {t('dashboard.hello', { name: managerProfile?.name || 'Manager' })}
                </h1>
                <p className="mt-1" style={{ color: 'rgba(255,255,255,0.45)', fontFamily: 'Inter, sans-serif', fontSize: '13px' }}>
                  {monthNameCap} — {t('dashboard.realTimeData')}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {managerServers?.map(s => <ServerBadge key={s} server={s} />)}
              </div>
            </div>
          </div>

          {/* SECTION 2 — PERFORMANCE CE MOIS */}
          <section>
            <h2 className="text-xs font-semibold mb-3 uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Sora, sans-serif' }}>
              {t('dashboard.performanceCeMois')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 md:gap-4">
              {serverPerf?.map(({ server, serverDiamonds, objective, pct, palier, bonusEstime, projection }) => {
                const color = SERVER_COLORS?.[server] || '#888';
                const barPct = pct != null ? Math.min(pct, 130) / 130 * 100 : 0;
                const progressColor = getProgressColor(pct);
                const atteignable = projection >= objective;
                const manque = objective > 0 ? Math.max(0, objective - projection) : 0;

                return (
                  <div key={server} className="rounded-xl border" style={{ backgroundColor: '#111111', borderColor: '#1f1f1f', padding: 'clamp(12px, 3vw, 20px)' }}>
                    <div className="flex items-center justify-between mb-3">
                      <ServerBadge server={server} />
                      {pct != null && (
                        <span className="text-xs font-bold" style={{ color: progressColor }}>
                          {pct?.toFixed(1)}%
                        </span>
                      )}
                    </div>
                    <div className="mb-1">
                      <span className="text-xl font-bold text-white" style={{ fontFamily: 'Sora, sans-serif' }}>
                        {fmtM(serverDiamonds)}
                      </span>
                      <span className="text-xs ml-1" style={{ color: 'rgba(255,255,255,0.35)' }}>💎</span>
                    </div>
                    {objective > 0 && (
                      <p className="text-xs mb-2" style={{ color: 'rgba(255,255,255,0.35)', fontFamily: 'Inter, sans-serif' }}>
                        {t('dashboard.objectif')}: {fmtM(objective)}
                      </p>
                    )}
                    <div className="w-full h-1.5 rounded-full mb-3" style={{ backgroundColor: 'rgba(255,255,255,0.08)' }}>
                      <div
                        className="h-full rounded-full transition-all"
                        style={{ width: `${barPct}%`, backgroundColor: progressColor }}
                      />
                    </div>
                    {palier && (
                      <p className="text-xs mb-1" style={{ color: '#22c55e', fontFamily: 'Inter, sans-serif' }}>
                        {t('dashboard.palierDetail', { pct: palier?.pct, bonus: palier?.bonus_pct || palier?.bonus || 0 })}
                      </p>
                    )}
                    {bonusEstime > 0 && (
                      <p className="text-xs mb-2" style={{ color: 'rgba(255,255,255,0.5)', fontFamily: 'Inter, sans-serif' }}>
                        {t('dashboard.bonusEstime')}: <span className="text-white font-semibold">{fmtUSD(bonusEstime)}</span>
                      </p>
                    )}
                    <div className="mt-2 pt-2 border-t" style={{ borderColor: '#1f1f1f' }}>
                      <p className="text-xs" style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Inter, sans-serif' }}>
                        {t('dashboard.projectionFinMois')}: <span className="text-white">{fmtM(projection)}</span>
                      </p>
                      {objective > 0 && (
                        <p className="text-xs mt-0.5" style={{ color: atteignable ? '#22c55e' : '#f97316', fontFamily: 'Inter, sans-serif' }}>
                          {atteignable ? t('dashboard.objectifAtteignable') : t('dashboard.manqueProjection', { val: fmtM(manque) })}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
              {serverPerf?.length === 0 && (
                <div className="col-span-4 text-center py-8" style={{ color: 'rgba(255,255,255,0.3)', fontFamily: 'Inter, sans-serif', fontSize: '14px' }}>
                  {t('dashboard.aucuneDonnee')}
                </div>
              )}
            </div>
          </section>

          {/* SECTION 3 — MES CRÉATEURS */}
          <section>
            <h2 className="text-xs font-semibold mb-3 uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Sora, sans-serif' }}>
              {t('nav.creators')}
            </h2>
            {/* 3 stat cards — 1 col mobile, 3 cols sm+ */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
              <div className="rounded-xl border" style={{ backgroundColor: '#111111', borderColor: '#1f1f1f', padding: 'clamp(12px, 3vw, 16px)' }}>
                <p className="text-xs mb-1" style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Inter, sans-serif' }}>{t('performance.activeMonth')}</p>
                <p className="text-2xl font-bold" style={{ fontFamily: 'Sora, sans-serif' }}>
                  <span style={{ color: '#22c55e' }}>{actifsCount}</span>
                  <span className="text-sm font-medium" style={{ color: 'rgba(255,255,255,0.4)' }}>/{totalCreateurs}</span>
                </p>
                <p className="text-xs font-bold mt-0.5" style={{ color: goLiveRate >= 50 ? '#22c55e' : goLiveRate >= 30 ? '#f97316' : '#ef4444', fontFamily: 'Sora, sans-serif' }}>
                  {goLiveRate}% Go LIVE
                </p>
              </div>
              <div className="rounded-xl border" style={{ backgroundColor: '#111111', borderColor: '#1f1f1f', padding: 'clamp(12px, 3vw, 16px)' }}>
                <p className="text-xs mb-1" style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Inter, sans-serif' }}>{t('creators.inactiveMonth')}</p>
                <p className="text-2xl font-bold" style={{ color: '#ef4444', fontFamily: 'Sora, sans-serif' }}>{inactifsCount}</p>
              </div>
              <div className="rounded-xl border" style={{ backgroundColor: '#111111', borderColor: '#1f1f1f', padding: 'clamp(12px, 3vw, 16px)' }}>
                <p className="text-xs mb-1" style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Inter, sans-serif' }}>{t('dashboard.avgDiamondsActive')}</p>
                <p className="text-2xl font-bold" style={{ color: '#f97316', fontFamily: 'Sora, sans-serif' }}>{fmtM(avgDiamonds)}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-4">
              {/* Tier distribution — M and M-1 in same chart */}
              <div className="rounded-xl border" style={{ backgroundColor: '#111111', borderColor: '#1f1f1f', padding: 'clamp(12px, 3vw, 20px)' }}>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Sora, sans-serif' }}>
                    {t('dashboard.distributionTier')}
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: 'var(--color-primary)' }} />
                      <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.5)' }}>{t('dashboard.currentMonth', 'Current month')}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: '#555555' }} />
                      <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.35)' }}>M-1</span>
                    </div>
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={tierDist} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                    <XAxis dataKey="tier" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10 }} axisLine={false} tickLine={false} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: '8px' }}
                      labelStyle={{ color: 'white', fontSize: 12, fontWeight: 700 }}
                      formatter={(value, name) => [value, name === 'current' ? t('dashboard.currentMonth', 'Current month') : 'M-1']}
                      itemStyle={{ fontSize: 11 }}
                    />
                    <Bar dataKey="previous" name="previous" fill="#555555" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="current" name="current" fill="#E5202A" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Level distribution */}
              <div className="rounded-xl border" style={{ backgroundColor: '#111111', borderColor: '#1f1f1f', padding: 'clamp(12px, 3vw, 20px)' }}>
                <p className="text-xs font-semibold mb-3 uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Sora, sans-serif' }}>
                  {t('dashboard.distributionNiveau')}
                </p>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  {Object.entries(levelDist)?.map(([level, count]) => (
                    <div key={level} className="flex items-center gap-2 p-3 rounded-lg" style={{ backgroundColor: LEVEL_CONFIG?.[level]?.bg || 'rgba(255,255,255,0.05)' }}>
                      <span className="text-xl font-bold" style={{ color: LEVEL_CONFIG?.[level]?.color, fontFamily: 'Sora, sans-serif' }}>{count}</span>
                      <span className="text-xs font-semibold" style={{ color: LEVEL_CONFIG?.[level]?.color, fontFamily: 'Inter, sans-serif' }}>{level}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* SECTION 4 — OBJECTIFS À DATE */}
          <section>
            <h2 className="text-xs font-semibold mb-3 uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Sora, sans-serif' }}>
              {t('performance.objectivesDate')}
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 md:gap-4">
              {/* Activeness card */}
              <ActivenessDistributionCard
                distribution={activenessDistribution}
                totalBonus={totalBonusActiveness}
                title={t('dashboard.activeness')}
              />

              {/* Rank-up card */}
              <div className="rounded-xl border" style={{ backgroundColor: '#111111', borderColor: '#1f1f1f', padding: 'clamp(12px, 3vw, 20px)' }}>
                <p className="text-xs font-semibold mb-3 uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Sora, sans-serif' }}>
                  {t('dashboard.rankup')}
                </p>
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <div className="text-center p-2 rounded-lg" style={{ backgroundColor: 'rgba(34,197,94,0.1)' }}>
                    <p className="text-lg font-bold" style={{ color: '#22c55e', fontFamily: 'Sora, sans-serif' }}>{rankingUp}</p>
                    <p className="text-xs" style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Inter, sans-serif' }}>↑ {t('dashboard.rankingUp')}</p>
                  </div>
                  <div className="text-center p-2 rounded-lg" style={{ backgroundColor: 'rgba(59,130,246,0.1)' }}>
                    <p className="text-lg font-bold" style={{ color: '#3b82f6', fontFamily: 'Sora, sans-serif' }}>{maintaining}</p>
                    <p className="text-xs" style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Inter, sans-serif' }}>→ {t('dashboard.maintaining')}</p>
                  </div>
                </div>
                <div className="pt-2 border-t" style={{ borderColor: '#1f1f1f' }}>
                  <p className="text-xs" style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Inter, sans-serif' }}>
                    {t('dashboard.totalBonusRankup')}: <span className="text-white font-semibold">{fmtUSD(totalBonusRankup)}</span>
                  </p>
                </div>
              </div>

              {/* Bonus total card */}
              <div className="rounded-xl border" style={{ backgroundColor: '#111111', borderColor: '#1f1f1f', padding: 'clamp(12px, 3vw, 20px)' }}>
                <p className="text-xs font-semibold mb-3 uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Sora, sans-serif' }}>
                  {t('performance.totalBonus')}
                </p>
                <div className="space-y-2 mb-3">
                  <div className="flex justify-between items-center">
                    <span className="text-xs" style={{ color: 'rgba(255,255,255,0.5)', fontFamily: 'Inter, sans-serif' }}>{t('dashboard.activeness')}</span>
                    <span className="text-sm text-white font-semibold" style={{ fontFamily: 'Sora, sans-serif' }}>{fmtUSD(totalBonusActiveness)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs" style={{ color: 'rgba(255,255,255,0.5)', fontFamily: 'Inter, sans-serif' }}>{t('dashboard.rankup')}</span>
                    <span className="text-sm text-white font-semibold" style={{ fontFamily: 'Sora, sans-serif' }}>{fmtUSD(totalBonusRankup)}</span>
                  </div>
                  <div className="flex justify-between items-center pt-2 border-t" style={{ borderColor: '#1f1f1f' }}>
                    <span className="text-sm font-bold text-white" style={{ fontFamily: 'Sora, sans-serif' }}>{t('dashboard.total')}</span>
                    <span className="text-lg font-bold" style={{ color: '#E5202A', fontFamily: 'Sora, sans-serif' }}>{fmtUSD(totalBonus)}</span>
                  </div>
                  <div className="flex justify-between items-center pt-2 border-t" style={{ borderColor: '#1f1f1f' }}>
                    <span className="text-sm font-bold" style={{ color: '#22c55e', fontFamily: 'Sora, sans-serif' }}>{t('dashboard.bonusNet')}</span>
                    <span className="text-lg font-bold" style={{ color: '#22c55e', fontFamily: 'Sora, sans-serif' }}>{fmtUSD(totalBonusNet)}</span>
                  </div>
                </div>
                <div className="pt-2 border-t" style={{ borderColor: '#1f1f1f' }}>
                  <p className="text-xs" style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Inter, sans-serif' }}>
                    {t('dashboard.projectionFinMois')}: <span className="text-white font-semibold">{fmtUSD(projectionBonus)}</span>
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* SECTION 5 — TOP 5 PERFORMERS */}
          {top5?.length > 0 && (
            <section>
              <h2 className="text-xs font-semibold mb-3 uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Sora, sans-serif' }}>
                {t('dashboard.top5Performers')}
              </h2>
              {/* Mobile: compact cards, Desktop: table rows */}
              <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: '#111111', borderColor: '#1f1f1f' }}>
                {top5?.map((creator, idx) => {
                  const rankUpStatus = (creator?.tier_current || 1) > (creator?.tier_m1 || 1) ? '↑' :
                    (creator?.tier_current || 1) < (creator?.tier_m1 || 1) ? '↓' : '→';
                  const rankColor = rankUpStatus === '↑' ? '#22c55e' : rankUpStatus === '↓' ? '#ef4444' : '#3b82f6';
                  return (
                    <div
                      key={creator?.tiktok_id}
                      className="flex items-center gap-3 cursor-pointer transition-colors border-b last:border-b-0"
                      style={{ borderColor: '#1f1f1f', padding: 'clamp(10px, 2vw, 14px) clamp(12px, 3vw, 20px)' }}
                      onClick={() => navigate(`/manager/creators/${creator?.tiktok_id}`)}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.03)'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <span className="text-xs font-bold w-5 text-center flex-shrink-0" style={{ color: rankColor }}>{rankUpStatus}</span>
                      <span className="text-xs font-bold w-5 text-center flex-shrink-0" style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Sora, sans-serif' }}>#{idx + 1}</span>
                      <span className="flex-1 text-sm font-semibold text-white truncate" style={{ fontFamily: 'Inter, sans-serif', minWidth: 0 }}>{creator?.username || creator?.tiktok_id}</span>
                      <ServerBadge server={creator?.server} />
                      <TierBadge tier={creator?.tier_current || 1} />
                      <span className="text-xs font-bold flex-shrink-0" style={{ color: '#FF6B00', fontFamily: 'Sora, sans-serif' }}>💎 {fmtM(creator?.total_diamonds)}</span>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* SECTION 6 — ALERTES (unified) */}
          {hasAlerts && (
            <section>
              <h2 className="text-xs font-semibold mb-3 uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Sora, sans-serif' }}>
                {t('dashboard.alertes')}
              </h2>
              <div className="space-y-3">
                {/* ⚡ Activeness — proches du palier suivant */}
                {activenessClose?.length > 0 && (
                  <div className="rounded-xl border-l-2 border w-full" style={{ backgroundColor: '#111111', borderColor: '#1f1f1f', borderLeftColor: '#22c55e', padding: 'clamp(12px, 3vw, 20px)' }}>
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-base">⚡</span>
                      <p className="text-sm font-semibold text-white" style={{ fontFamily: 'Sora, sans-serif' }}>
                        {t('dashboard.activenessAlerte')} ({activenessClose.length})
                      </p>
                    </div>
                    <div className="space-y-1">
                      {activenessClose.map(c => (
                        <div key={c.tiktok_id} onClick={() => navigate(`/manager/creators/${c.tiktok_id}`)}
                          className="flex items-center justify-between px-2 py-2 rounded-lg transition-colors cursor-pointer min-h-[44px]"
                          onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(34,197,94,0.08)'}
                          onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                          <span className="text-sm text-white truncate flex-1" style={{ fontFamily: 'Inter', minWidth: 0 }}>{c.username}</span>
                          <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                            <span className="text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>L{c.activeness_level}→L{c.next_level}</span>
                            <span className="text-xs font-bold px-1.5 py-0.5 rounded" style={{ backgroundColor: 'rgba(34,197,94,0.12)', color: '#22c55e' }}>
                              {c.days_gap > 0 ? `${c.days_gap}${t('admin.dashboard.daysShort', 'd')}` : ''}{c.days_gap > 0 && c.hours_gap > 0 ? ' · ' : ''}{c.hours_gap > 0 ? `${c.hours_gap}h` : ''}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* 🛡️ Maintien — risque de perte de tier */}
                {nearMaintain?.length > 0 && (
                  <div className="rounded-xl border-l-2 border w-full" style={{ backgroundColor: '#111111', borderColor: '#1f1f1f', borderLeftColor: '#f59e0b', padding: 'clamp(12px, 3vw, 20px)' }}>
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-base">🛡️</span>
                      <p className="text-sm font-semibold text-white" style={{ fontFamily: 'Sora, sans-serif' }}>
                        {t('dashboard.nearMaintainAlerte')} ({nearMaintain.length})
                      </p>
                    </div>
                    <div className="space-y-1">
                      {nearMaintain.map(c => (
                        <div key={c.tiktok_id} onClick={() => navigate(`/manager/creators/${c.tiktok_id}`)}
                          className="flex items-center justify-between px-2 py-2 rounded-lg transition-colors cursor-pointer min-h-[44px]"
                          onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(245,158,11,0.08)'}
                          onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                          <span className="text-sm text-white truncate flex-1" style={{ fontFamily: 'Inter', minWidth: 0 }}>{c.username}</span>
                          <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                            <span className="text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>T{c.tier_m1}</span>
                            <span className="text-xs font-bold px-1.5 py-0.5 rounded" style={{ backgroundColor: 'rgba(245,158,11,0.12)', color: '#f59e0b' }}>
                              {t('dashboard.kMissing', { k: (c.maintain_gap / 1000).toFixed(0) })}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* 🎯 Rankup — proches du tier suivant */}
                {nearNextTier?.length > 0 && (
                  <div className="rounded-xl border-l-2 border w-full" style={{ backgroundColor: '#111111', borderColor: '#1f1f1f', borderLeftColor: '#f97316', padding: 'clamp(12px, 3vw, 20px)' }}>
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-base">🎯</span>
                      <p className="text-sm font-semibold text-white" style={{ fontFamily: 'Sora, sans-serif' }}>
                        {t('dashboard.nearTierAlerte')} ({nearNextTier.length})
                      </p>
                    </div>
                    <div className="space-y-1">
                      {nearNextTier.map(c => (
                        <div key={c.tiktok_id} onClick={() => navigate(`/manager/creators/${c.tiktok_id}`)}
                          className="flex items-center justify-between px-2 py-2 rounded-lg transition-colors cursor-pointer min-h-[44px]"
                          onMouseEnter={e => e.currentTarget.style.backgroundColor = c.alertType === 'condition' ? 'rgba(168,85,247,0.08)' : 'rgba(249,115,22,0.08)'}
                          onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                          <span className="text-sm text-white truncate flex-1" style={{ fontFamily: 'Inter', minWidth: 0 }}>{c.username}</span>
                          <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                            {c.alertType === 'condition' ? (
                              <>
                                <span className="text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>T{c.tier_m1}→T{c.tier_current}</span>
                                <span className="text-xs font-bold px-1.5 py-0.5 rounded" style={{ backgroundColor: 'rgba(168,85,247,0.12)', color: '#a855f7' }}>
                                  {c.missingDays > 0 ? `${c.missingDays}${t('admin.dashboard.daysShort', 'd')}` : ''}{c.missingDays > 0 && c.missingHours > 0 ? ' · ' : ''}{c.missingHours > 0 ? `${c.missingHours}h` : ''}
                                </span>
                              </>
                            ) : (
                              <>
                                <span className="text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>T{c.tier_m1}→T{(c.tier_m1||0)+1}</span>
                                <span className="text-xs font-bold px-1.5 py-0.5 rounded" style={{ backgroundColor: 'rgba(249,115,22,0.12)', color: '#f97316' }}>
                                  {t('dashboard.kMissing', { k: (c.gap / 1000).toFixed(0) })}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* 😴 Inactifs */}
                {inactifs?.length > 0 && (
                  <div className="rounded-xl border-l-2 border w-full" style={{ backgroundColor: '#111111', borderColor: '#1f1f1f', borderLeftColor: '#ef4444', padding: 'clamp(12px, 3vw, 20px)' }}>
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-base">😴</span>
                      <p className="text-sm font-semibold text-white" style={{ fontFamily: 'Sora, sans-serif' }}>
                        {t('dashboard.inactifsAlerte')} ({inactifs?.length})
                      </p>
                    </div>
                    <div className="space-y-1">
                      {inactifs?.slice(0, 5)?.map(c => (
                        <div key={c.tiktok_id} onClick={() => navigate(`/manager/creators/${c.tiktok_id}`)}
                          className="flex items-center justify-between px-2 py-2 rounded-lg transition-colors cursor-pointer min-h-[44px]"
                          onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(239,68,68,0.08)'}
                          onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                          <span className="text-sm text-white truncate flex-1" style={{ fontFamily: 'Inter', minWidth: 0 }}>{c.username || c.tiktok_id}</span>
                          <ServerBadge server={c.server} />
                        </div>
                      ))}
                    </div>
                    {inactifs?.length > 5 && (
                      <button onClick={() => navigate('/manager/creators?filter=inactive')} className="mt-3 text-xs underline" style={{ color: '#3b82f6', fontFamily: 'Inter' }}>
                        {t('dashboard.voirTous')} ({inactifs?.length})
                      </button>
                    )}
                  </div>
                )}

                {/* 📉 Régression */}
                {inRegression?.length > 0 && (
                  <div className="rounded-xl border-l-2 border w-full" style={{ backgroundColor: '#111111', borderColor: '#1f1f1f', borderLeftColor: '#ef4444', padding: 'clamp(12px, 3vw, 20px)' }}>
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-base">📉</span>
                      <p className="text-sm font-semibold text-white" style={{ fontFamily: 'Sora, sans-serif' }}>
                        {t('dashboard.regressionAlerte')} ({inRegression?.length})
                      </p>
                    </div>
                    <div className="space-y-1">
                      {inRegression?.slice(0, 5)?.map(c => (
                        <div key={c.tiktok_id} onClick={() => navigate(`/manager/creators/${c.tiktok_id}`)}
                          className="flex items-center justify-between px-2 py-2 rounded-lg transition-colors cursor-pointer min-h-[44px]"
                          onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(239,68,68,0.08)'}
                          onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                          <span className="text-sm text-white truncate flex-1" style={{ fontFamily: 'Inter', minWidth: 0 }}>{c.username}</span>
                          <span className="text-xs font-bold px-1.5 py-0.5 rounded" style={{ backgroundColor: 'rgba(239,68,68,0.12)', color: '#ef4444' }}>
                            {Math.round(c.evolution_pct)}%
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* 📄 Contrats expirants */}
                {contractExpiring?.length > 0 && (
                  <div className="rounded-xl border-l-2 border w-full" style={{ backgroundColor: '#111111', borderColor: '#1f1f1f', borderLeftColor: '#8b5cf6', padding: 'clamp(12px, 3vw, 20px)' }}>
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-base">📄</span>
                      <p className="text-sm font-semibold text-white" style={{ fontFamily: 'Sora, sans-serif' }}>
                        {t('dashboard.contratsAlerte')} ({contractExpiring?.length})
                      </p>
                    </div>
                    <div className="space-y-1">
                      {contractExpiring?.map(c => {
                        const daysLeft = Math.ceil((new Date(c.contract_end) - new Date()) / 86400000);
                        return (
                          <div key={c.tiktok_id} onClick={() => navigate(`/manager/creators/${c.tiktok_id}`)}
                            className="flex items-center justify-between px-2 py-2 rounded-lg transition-colors cursor-pointer min-h-[44px]"
                            onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(139,92,246,0.08)'}
                            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                            <span className="text-sm text-white truncate flex-1" style={{ fontFamily: 'Inter', minWidth: 0 }}>{c.username}</span>
                            <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                              <span className="text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>{new Date(c.contract_end).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })}</span>
                              <span className="text-xs font-bold px-1.5 py-0.5 rounded" style={{ backgroundColor: daysLeft <= 7 ? 'rgba(239,68,68,0.15)' : 'rgba(249,115,22,0.12)', color: daysLeft <= 7 ? '#ef4444' : '#f97316' }}>
                                {daysLeft}{t('admin.dashboard.daysShort', 'd')}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* SECTION 7b — VIOLATIONS */}
          {(violationsLoading || violationStats.activeBans > 0 || violationStats.thisMonth > 0) && (
            <section>
              <h2 className="text-xs font-semibold mb-3 uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Sora, sans-serif' }}>
                Violations
              </h2>
              <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: '#111111', borderColor: '#1f1f1f' }}>
                <div className="px-5 py-4 border-b flex items-center justify-between" style={{ borderColor: '#1f1f1f' }}>
                  <div>
                    <h3 className="text-sm font-bold text-white" style={{ fontFamily: 'Sora, sans-serif' }}>
                      ⚠️ {t('dashboard.violations', 'Violations')}
                    </h3>
                    <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.35)', fontFamily: 'Inter, sans-serif' }}>
                      {t('dashboard.violationsDesc', 'Mois en cours')}
                    </p>
                  </div>
                  <button
                    onClick={() => navigate('/manager/violations')}
                    className="px-3 py-1 rounded-lg text-xs font-semibold"
                    style={{ backgroundColor: 'rgba(239,68,68,0.12)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.25)', cursor: 'pointer', fontFamily: 'Inter, sans-serif' }}
                  >
                    {t('dashboard.voirTous', 'Voir tout')}
                  </button>
                </div>

                {violationsLoading ? (
                  <div className="p-4 space-y-2">{[1,2,3].map(i => <div key={i} className="h-8 rounded animate-pulse" style={{ backgroundColor: '#1a1a1a' }} />)}</div>
                ) : (
                  <>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 p-4">
                      <div className="text-center">
                        <p className="text-lg font-bold" style={{ color: '#f97316', fontFamily: 'Sora, sans-serif' }}>{violationStats.thisMonth}</p>
                        <p className="text-xs" style={{ color: 'rgba(255,255,255,0.35)', fontFamily: 'Inter, sans-serif' }}>{t('dashboard.violationsThisMonth', 'Ce mois')}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-bold" style={{ color: '#ef4444', fontFamily: 'Sora, sans-serif' }}>{violationStats.activeBans}</p>
                        <p className="text-xs" style={{ color: 'rgba(255,255,255,0.35)', fontFamily: 'Inter, sans-serif' }}>{t('dashboard.activeBans', 'Bans actifs')}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-bold" style={{ color: '#fbbf24', fontFamily: 'Sora, sans-serif' }}>{violationStats.notAppealed}</p>
                        <p className="text-xs" style={{ color: 'rgba(255,255,255,0.35)', fontFamily: 'Inter, sans-serif' }}>{t('dashboard.notAppealed', 'Not appealed')}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-bold" style={{ color: '#22c55e', fontFamily: 'Sora, sans-serif' }}>{violationStats.withdrawn}</p>
                        <p className="text-xs" style={{ color: 'rgba(255,255,255,0.35)', fontFamily: 'Inter, sans-serif' }}>{t('dashboard.withdrawn', 'Withdrawn')}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-bold" style={{ color: '#3b82f6', fontFamily: 'Sora, sans-serif' }}>{violationStats.ticketsOpened}</p>
                        <p className="text-xs" style={{ color: 'rgba(255,255,255,0.35)', fontFamily: 'Inter, sans-serif' }}>{t('dashboard.ticketsOpened', 'Tickets')}</p>
                      </div>
                    </div>

                    {activeBansList.length > 0 && (
                      <div className="border-t" style={{ borderColor: '#1f1f1f' }}>
                        {activeBansList.map((v, i) => (
                          <div
                            key={v.id}
                            onClick={() => navigate('/manager/violations')}
                            className="flex items-center justify-between px-4 py-2.5 cursor-pointer transition-colors"
                            style={{ borderBottom: i < activeBansList.length - 1 ? '1px solid #1a1a1a' : 'none' }}
                            onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.03)'}
                            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            <div className="min-w-0">
                              <p className="text-sm text-white truncate" style={{ fontFamily: 'Inter' }}>{v.creator_nickname || v.creator_username}</p>
                              <p className="text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>@{v.creator_username} · {v.server} · {v.violation_type}</p>
                            </div>
                            <span className="text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ml-2" style={{ backgroundColor: 'rgba(239,68,68,0.12)', color: '#ef4444', fontFamily: 'Inter, sans-serif' }}>
                              In effect
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </section>
          )}

          {/* SECTION 8 — WHATSAPP ALERTS */}
          {(whatsappAlertsLoading || whatsappAlerts?.length > 0) && (
            <section>
              <h2 className="text-xs font-semibold mb-3 uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Sora, sans-serif' }}>
                WhatsApp
              </h2>
              <div className="rounded-xl border-l-2 border w-full" style={{ backgroundColor: '#111111', borderColor: '#1f1f1f', borderLeftColor: '#f97316', padding: 'clamp(12px, 3vw, 20px)' }}>
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-base">📵</span>
                  <p className="text-sm font-semibold text-white" style={{ fontFamily: 'Sora, sans-serif' }}>
                    {t('dashboard.sansWhatsapp')} {!whatsappAlertsLoading && `(${whatsappAlerts?.length})`}
                  </p>
                </div>
                {whatsappAlertsLoading ? (
                  <div className="space-y-2">
                    {[...Array(3)]?.map((_, i) => (
                      <div key={i} className="h-10 rounded-lg animate-pulse" style={{ backgroundColor: '#1f1f1f' }} />
                    ))}
                  </div>
                ) : whatsappAlerts?.length === 0 ? (
                  <p className="text-xs" style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Inter, sans-serif' }}>
                    {t('dashboard.tousWhatsapp')}
                  </p>
                ) : (
                  <>
                    <div className="space-y-2">
                      {whatsappAlerts?.slice(0, 5)?.map(c => (
                        <div
                          key={c?.tiktok_id}
                          className="flex items-center gap-2 px-2 py-2 rounded-lg transition-colors min-h-[44px]"
                          style={{ cursor: 'pointer' }}
                          onClick={() => navigate(`/manager/creators/${c?.tiktok_id}`)}
                          onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'rgba(249,115,22,0.08)'; }}
                          onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                        >
                          <span className="text-sm text-white truncate flex-1" style={{ fontFamily: 'Inter, sans-serif', minWidth: 0 }}>{c?.username || c?.tiktok_id}</span>
                          {c?.server && <ServerBadge server={c?.server} />}
                          <span className="text-xs flex-shrink-0" style={{ color: '#f97316', fontFamily: 'Inter, sans-serif' }}>{t('dashboard.noNumber')}</span>
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={() => navigate('/manager/creators?filter=sans_whatsapp')}
                      className="mt-3 text-xs underline"
                      style={{ color: '#3b82f6', fontFamily: 'Inter, sans-serif' }}
                    >
                      {t('dashboard.voirTous')} ({whatsappAlerts?.length})
                    </button>
                  </>
                )}
              </div>
            </section>
          )}

        </div>
      </main>
      <ManagerBottomNav />
      <PushPrompt />
    </div>
  );
};

export default ManagerDashboard;
